import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// ── Public Checkout Page — aspreo.com/buy/{slug} ─────────────────────────────
// Flow: Product info → Buyer fills form → Pay → Confirmation + Delivery
const FONT = "'Inter',Arial,sans-serif";

export default function PublicCheckoutPage() {
  const slug = window.location.pathname.split('/buy/')[1];
  const [product,  setProduct]  = useState(null);
  const [seller,   setSeller]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [step,     setStep]     = useState('form'); // form | paying | success
  const [orderId,  setOrderId]  = useState(null);

  const [form, setForm] = useState({ buyer_name:'', buyer_email:'', buyer_phone:'', fields:{} });
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const updF = (k,v) => setForm(p=>({...p,fields:{...p.fields,[k]:v}}));

  useEffect(() => {
    if (!slug) { setError('Invalid checkout link.'); setLoading(false); return; }
    (async () => {
      const { data: prod } = await supabase.from('products').select('*').eq('slug', slug).eq('is_active', true).maybeSingle();
      if (!prod) { setError('This product is no longer available.'); setLoading(false); return; }
      // Check max_orders
      if (prod.max_orders) {
        const { count } = await supabase.from('orders').select('id', { count:'exact' }).eq('product_id', prod.id).eq('status','paid');
        if (count >= prod.max_orders) { setError('This product is sold out.'); setLoading(false); return; }
      }
      setProduct(prod);
      const { data: sel } = await supabase.from('profiles').select('full_name,biz_name,biz_logo_url,biz_tagline,razorpay_key_id').eq('id', prod.user_id).maybeSingle();
      setSeller(sel || {});
      setLoading(false);
    })();
  }, [slug]);

  const handlePay = async () => {
    if (!form.buyer_name.trim() || !form.buyer_email.trim()) return;
    setStep('paying');

    // Create order via Edge Function (bypasses RLS - works for unauthenticated guests)
    const createRes = await fetch(
      `/api/sb/functions/v1/create-order`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}`,
        },
        body: JSON.stringify({
          product_id:     product.id,
          user_id:        product.user_id,
          buyer_name:     form.buyer_name,
          buyer_email:    form.buyer_email,
          buyer_phone:    form.buyer_phone,
          buyer_fields:   form.fields,
          amount:         parseFloat(product.price),
          currency:       product.currency || 'INR',
          payment_method: seller.razorpay_key_id ? 'razorpay' : 'manual',
        }),
      }
    );
    const createData = await createRes.json();
    if (!createRes.ok || !createData.order) {
      setStep('form');
      alert('Failed to create order: ' + (createData.error || 'Please try again.'));
      return;
    }
    const order = createData.order;
    setOrderId(order.id);

    if (seller.razorpay_key_id) {
      // Razorpay payment
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        document.head.appendChild(script);
        await new Promise(res => script.onload = res);
      }
      const rzp = new window.Razorpay({
        key:         seller.razorpay_key_id,
        amount:      Math.round(parseFloat(product.price) * 100), // paise
        currency:    product.currency || 'INR',
        name:        seller.biz_name || seller.full_name || 'Aspreo',
        description: product.name,
        image:       seller.biz_logo_url || '',
        prefill: {
          name:    form.buyer_name,
          email:   form.buyer_email,
          contact: form.buyer_phone,
        },
        notes: { order_id: order.id, product_id: product.id },
        handler: async (response) => {
          // Payment success — call edge function to update + deliver
          // (anon key cannot UPDATE orders directly — server-side handles it)
          try {
            await fetch('/api/sb/functions/v1/process-delivery', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}` },
              body: JSON.stringify({
                order_id:            order.id,
                product_id:          product.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_signature:  response.razorpay_signature,
                mark_paid:           true,
              }),
            });
          } catch(e) {
            console.warn('[checkout] process-delivery:', e.message);
          }
          setStep('success');
        },
        modal: { ondismiss: () => setStep('form') },
        theme: { color: '#7C3AED' },
      });
      rzp.open();
    } else {
      // No Razorpay — demo mode / manual payment
      // Trigger delivery via edge function (handles update + email server-side)
      fetch('/api/sb/functions/v1/process-delivery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}` },
        body: JSON.stringify({ order_id: order.id, product_id: product.id, mark_paid: false }),
      }).catch(()=>{});
      setStep('success');
    }
  };

  const s  = { minHeight:'100vh', background:'#F6F6F8', fontFamily:FONT, padding:'32px 16px' };
  const card = { background:'#fff', borderRadius:16, maxWidth:480, margin:'0 auto', boxShadow:'0 8px 32px rgba(124,58,237,.10)', overflow:'hidden' };
  const inp  = (ph,val,onChange,type='text') => (
    <input type={type} value={val} onChange={onChange} placeholder={ph} required
      style={{width:'100%',padding:'11px 14px',border:'1.5px solid #E4E4EC',borderRadius:9,fontFamily:FONT,fontSize:14,outline:'none',boxSizing:'border-box',color:'#0F0E1A',marginBottom:10}}
      onFocus={e=>e.target.style.borderColor='#7C3AED'} onBlur={e=>e.target.style.borderColor='#E4E4EC'}/>
  );

  if (loading) return (
    <div style={{...s,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:44,height:44,border:'3px solid #7C3AED',borderTopColor:'transparent',borderRadius:'50%',animation:'spin .8s linear infinite',margin:'0 auto 12px'}}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{color:'#4A4A6A'}}>Loading…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{...s,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{...card,padding:48,textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:12}}>🛍</div>
        <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Not Available</div>
        <div style={{color:'#4A4A6A',fontSize:14,marginBottom:24}}>{error}</div>
        <a href="https://www.aspreo.com" style={{color:'#7C3AED',fontWeight:700,textDecoration:'none'}}>← Back to Aspreo</a>
      </div>
    </div>
  );

  return (
    <div style={s}>
      {/* Seller header */}
      <div style={{textAlign:'center',marginBottom:20}}>
        {seller.biz_logo_url&&<img src={seller.biz_logo_url} alt="logo" style={{height:48,marginBottom:8,objectFit:'contain'}}/>}
        <div style={{fontSize:15,fontWeight:700,color:'#0F0E1A'}}>{seller.biz_name||seller.full_name||''}</div>
        <div style={{fontSize:11,color:'#8888AA',marginTop:4}}>Powered by <strong style={{color:'#7C3AED'}}>Aspreo</strong></div>
      </div>

      <div style={card}>
        {/* Product info */}
        {product.cover_image_url&&<img src={product.cover_image_url} alt={product.name} style={{width:'100%',height:200,objectFit:'cover'}}/>}
        <div style={{padding:'20px 24px',borderBottom:'1px solid #F0F0F8'}}>
          <div style={{fontWeight:800,fontSize:20,color:'#0F0E1A',marginBottom:4}}>{product.name}</div>
          {product.description&&<div style={{fontSize:13,color:'#4A4A6A',lineHeight:1.6,marginBottom:8}}>{product.description}</div>}
          <div style={{fontSize:28,fontWeight:900,color:'#7C3AED'}}>
            ₹{parseFloat(product.price).toLocaleString('en-IN')}
            <span style={{fontSize:14,color:'#8888AA',fontWeight:400}}> one-time</span>
          </div>
          {/* What they get */}
          {product.delivery_type&&product.delivery_type!=='manual'&&(
            <div style={{background:'#F0FDF4',border:'1px solid #16A34A',borderRadius:8,padding:'8px 12px',marginTop:10,fontSize:12,color:'#14532D'}}>
              ✅ Delivered instantly after payment — {
                product.delivery_type==='drive_link'?'Download link sent to your email':
                product.delivery_type==='course_link'?'Course access sent to your email':
                product.delivery_type==='email'?'Details sent to your email':
                'Confirmation sent to your email'
              }
            </div>
          )}
        </div>

        {step==='form'&&(
          <div style={{padding:'20px 24px'}}>
            <div style={{fontWeight:700,fontSize:14,color:'#0F0E1A',marginBottom:14}}>Your Details</div>
            {inp('Full Name *', form.buyer_name, e=>upd('buyer_name',e.target.value))}
            {inp('Email Address *', form.buyer_email, e=>upd('buyer_email',e.target.value), 'email')}
            {inp('Phone Number', form.buyer_phone, e=>upd('buyer_phone',e.target.value), 'tel')}
            {/* Custom fields */}
            {(product.checkout_fields||[]).map(f=>(
              <div key={f.id} style={{marginBottom:10}}>
                <div style={{fontSize:12,color:'#4A4A6A',marginBottom:4,fontWeight:600}}>{f.label}{f.required&&<span style={{color:'#DC2626'}}> *</span>}</div>
                {inp(f.label, form.fields[f.label]||'', e=>updF(f.label,e.target.value))}
              </div>
            ))}

            {!seller.razorpay_key_id&&(
              <div style={{background:'#FFFBEB',border:'1px solid #D97706',borderRadius:8,padding:'8px 12px',marginBottom:12,fontSize:12,color:'#92400E'}}>
                ⚠️ Demo mode — no payment will be charged. This is a test checkout.
              </div>
            )}

            <button onClick={handlePay} disabled={!form.buyer_name.trim()||!form.buyer_email.trim()}
              style={{width:'100%',padding:'14px',background:'#7C3AED',color:'#fff',border:'none',borderRadius:10,fontFamily:FONT,fontSize:16,fontWeight:700,cursor:'pointer',opacity:(!form.buyer_name.trim()||!form.buyer_email.trim())?0.5:1,transition:'opacity .15s'}}>
              {seller.razorpay_key_id?`Pay ₹${parseFloat(product.price).toLocaleString('en-IN')}`:'Complete Order (Demo)'}
            </button>
            <div style={{fontSize:11,color:'#8888AA',textAlign:'center',marginTop:8}}>
              🔒 Secure checkout via {seller.razorpay_key_id?'Razorpay':'Aspreo'} · Your data is safe
            </div>
          </div>
        )}

        {step==='paying'&&(
          <div style={{padding:'40px 24px',textAlign:'center'}}>
            <div style={{width:44,height:44,border:'3px solid #7C3AED',borderTopColor:'transparent',borderRadius:'50%',animation:'spin .8s linear infinite',margin:'0 auto 16px'}}/>
            <div style={{fontWeight:700,fontSize:16,color:'#0F0E1A',marginBottom:4}}>Processing Payment…</div>
            <div style={{fontSize:13,color:'#4A4A6A'}}>Please complete the payment in the Razorpay window</div>
          </div>
        )}

        {step==='success'&&(
          <div style={{padding:'32px 24px',textAlign:'center'}}>
            <div style={{width:64,height:64,borderRadius:'50%',background:'#F0FDF4',border:'2px solid #16A34A',display:'flex',alignItems:'center',justifyContent:'center',fontSize:32,margin:'0 auto 16px'}}>✅</div>
            <div style={{fontSize:22,fontWeight:800,color:'#0F0E1A',marginBottom:6}}>
              {product.delivery_type==='manual'?'Order Placed!':'You\'re in! 🎉'}
            </div>
            <div style={{fontSize:14,color:'#4A4A6A',marginBottom:20,lineHeight:1.6}}>
              {product.delivery_type==='manual'
                ? `Thank you ${form.buyer_name}! We'll be in touch shortly to complete your order.`
                : `Thank you ${form.buyer_name}! Check ${form.buyer_email} — your ${product.delivery_type==='course_link'?'course access':'download'} has been sent.`
              }
            </div>
            <div style={{background:'#F5F3FF',border:'1px solid #DDD6FE',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#5B21B6'}}>
              <strong>{product.name}</strong><br/>
              <span style={{fontSize:12,color:'#7C3AED'}}>₹{parseFloat(product.price).toLocaleString('en-IN')} · {form.buyer_email}</span>
            </div>
            <div style={{fontSize:11,color:'#8888AA'}}>
              Powered by <strong style={{color:'#7C3AED'}}>Aspreo</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
